/**
 * Get available machine options excluding already selected machines
 * @param {Array} machineTrainingSections - Current machine training sections
 * @param {number} currentIndex - Index of current section
 * @param {Array} machineOptions - All available machine options
 * @returns {Array} Filtered machine options
 */
export const getAvailableMachineOptions = (machineTrainingSections, currentIndex, machineOptions) => {
  const selectedMachineIds = machineTrainingSections
    .map((section, index) => (index !== currentIndex ? section.machineId : null))
    .filter((id) => id !== null);

  return machineOptions.filter((option) => !selectedMachineIds.includes(option.value));
};

/**
 * Get training options for a specific machine
 * @param {string|number} machineId - ID of the machine
 * @param {Array} machines - All available machines
 * @returns {Array} Training options for the machine
 */
export const getTrainingOptions = (machineId, machines) => {
  if (!machineId) return [];
  const machine = machines.find((machine) => machine.id === machineId);
  return machine ? machine.trainings.map((training) => ({ value: training.id, name: training.name })) : [];
};

/**
 * Create formatted training machine list from sections
 * @param {Array} machineTrainingSections - Machine training sections
 * @param {Array} machines - All available machines
 * @returns {Object} Formatted machines with trainings
 */
export const createTrainingMachineList = (machineTrainingSections, machines) => {
  const normalizeToArray = (value) => (Array.isArray(value) ? value : value != null ? [value] : []);

  const machinesById = new Map(machines.map((machine) => [String(machine.id), machine]));
  const formattedMachines = new Map();

  for (const section of machineTrainingSections) {
    if (!section?.machineId) continue;

    const currentMachine = machinesById.get(String(section.machineId));
    if (!currentMachine) continue;

    if (!formattedMachines.has(String(currentMachine.id))) {
      formattedMachines.set(String(currentMachine.id), {
        id: currentMachine.id,
        code: currentMachine.code,
        name: currentMachine.name,
        trainings: [],
      });
    }

    const machineEntry = formattedMachines.get(String(currentMachine.id));
    const existingTrainings = new Set(machineEntry.trainings.map((t) => String(t.combinedTrainingId)));

    const selectedTrainingIds = normalizeToArray(section.trainingId);

    for (const trainingId of selectedTrainingIds) {
      if (trainingId == null) continue;

      const foundTraining = (currentMachine.trainings || []).find((t) => String(t.id) === String(trainingId));
      if (!foundTraining) continue;

      const combinedTrainingId = foundTraining.combinedTrainingId ?? foundTraining.id;
      const key = String(combinedTrainingId);
      if (existingTrainings.has(key)) continue;

      machineEntry.trainings.push({
        combinedTrainingId,
        code: foundTraining.code,
        name: foundTraining.name,
      });
      existingTrainings.add(key);
    }
  }

  return {
    machines: Array.from(formattedMachines.values()),
  };
};

/**
 * Handler for machine change
 * @param {number} index - Index of the section
 * @param {string|number} id - Machine ID
 * @param {Function} setMachineTrainingSections - State setter function
 */
export const handleChangeMachine = (index, id, setMachineTrainingSections) =>
  setMachineTrainingSections((previous) =>
    previous.map((section, idx) => (idx === index ? { machineId: id, trainingId: null } : section))
  );

/**
 * Handler for training change
 * @param {number} index - Index of the section
 * @param {string|number} id - Training ID
 * @param {Function} setMachineTrainingSections - State setter function
 */
export const handleChangeTraining = (index, id, setMachineTrainingSections) =>
  setMachineTrainingSections((previous) =>
    previous.map((section, idx) => (idx === index ? { ...section, trainingId: id } : section))
  );

/**
 * Handler for adding a new block
 * @param {number} index - Index where to add the new block
 * @param {Function} setMachineTrainingSections - State setter function
 */
export const handleAddBlock = (index, setMachineTrainingSections) =>
  setMachineTrainingSections((previous) => {
    const next = [...previous];
    next.splice(index + 1, 0, { machineId: null, trainingId: null });
    return next;
  });

/**
 * Handler for removing a block
 * @param {number} index - Index of the block to remove
 * @param {Function} setMachineTrainingSections - State setter function
 */
export const handleRemoveBlock = (index, setMachineTrainingSections) =>
  setMachineTrainingSections((previous) =>
    previous.length > 1 ? previous.filter((_, idx) => idx !== index) : [{ machineId: null, trainingId: null }]
  );
